import { Box, Chip, Typography } from "@mui/material";
import React from "react";

export type JsonValueProps = {
  value: unknown;
  /** @internal recursion depth */
  depth?: number;
};

const KEY_SX = {
  color: "#999",
  fontSize: 11,
  lineHeight: "16px",
  whiteSpace: "nowrap" as const,
};
const VAL_SX = { fontSize: 12, lineHeight: "16px", fontFamily: "inherit" };
const MAX_DEPTH = 3;

function isPrimitive(v: unknown) {
  return v === null || v === undefined || typeof v !== "object";
}

export const JsonValue: React.FC<JsonValueProps> = ({ value, depth = 0 }) => {
  // ── null / undefined / empty ─────────────────────────────────────
  if (value === null || value === undefined || value === "") {
    return (
      <Chip
        variant="outlined"
        size="small"
        label="–"
        sx={{
          height: 16,
          fontSize: 11,
          px: 0.5,
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
    );
  }

  // ── boolean ──────────────────────────────────────────────────────
  if (typeof value === "boolean") {
    return (
      <Chip
        size="small"
        label={value ? "igen" : "nem"}
        color={value ? "success" : "default"}
        sx={{ height: 16, fontSize: 11, "& .MuiChip-label": { px: 0.75 } }}
      />
    );
  }

  // ── number ───────────────────────────────────────────────────────
  if (typeof value === "number") {
    return (
      <Typography
        component="span"
        variant="caption"
        sx={{ fontFamily: "monospace" }}
      >
        {value}
      </Typography>
    );
  }

  // ── string: try to parse as JSON ─────────────────────────────────
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed !== null && typeof parsed === "object") {
        return <JsonValue value={parsed} depth={depth} />;
      }
    } catch {
      // plain string
    }
    return (
      <Typography component="span" variant="caption">
        {value}
      </Typography>
    );
  }

  // ── depth guard: fall back to compact stringify ──────────────────
  if (depth >= MAX_DEPTH) {
    return (
      <Typography
        component="span"
        variant="caption"
        sx={{ fontFamily: "monospace", color: "text.secondary" }}
      >
        {JSON.stringify(value)}
      </Typography>
    );
  }

  // ── array ────────────────────────────────────────────────────────
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography component="span" variant="caption" color="text.disabled">
          (üres)
        </Typography>
      );
    }

    // all primitives → inline comma list
    if (value.every(isPrimitive)) {
      return (
        <Typography component="span" variant="caption">
          {value.map(String).join(", ")}
        </Typography>
      );
    }

    // contains objects → numbered rows
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
        {value.map((item, i) => (
          <Box
            key={i}
            sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{ ...KEY_SX, minWidth: 16 }}
            >
              {i + 1}.
            </Typography>
            <JsonValue value={item} depth={depth + 1} />
          </Box>
        ))}
      </Box>
    );
  }

  // ── object ───────────────────────────────────────────────────────
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== undefined,
    );

    if (entries.length === 0) {
      return (
        <Typography component="span" variant="caption" color="text.disabled">
          (üres)
        </Typography>
      );
    }

    const allLeaf = entries.every(([, v]) => isPrimitive(v));

    if (allLeaf) {
      // inline: key: val · key: val
      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: "6px",
            rowGap: "1px",
            alignItems: "center",
          }}
        >
          {entries.map(([k, v], i) => (
            <React.Fragment key={k}>
              {i > 0 && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#ccc", userSelect: "none" }}
                >
                  ·
                </Typography>
              )}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  gap: "3px",
                  alignItems: "center",
                }}
              >
                <Typography component="span" variant="caption" sx={KEY_SX}>
                  {k}:
                </Typography>
                <Typography component="span" variant="caption" sx={VAL_SX}>
                  {v === null || v === undefined ? "–" : String(v)}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      );
    }

    // stacked: key per row, value may be complex
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {entries.map(([k, v]) => (
          <Box
            key={k}
            sx={{ display: "flex", gap: "4px", alignItems: "flex-start" }}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{ ...KEY_SX, pt: "1px" }}
            >
              {k}:
            </Typography>
            <JsonValue value={v} depth={depth + 1} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Typography component="span" variant="caption">
      {String(value)}
    </Typography>
  );
};
